/**
 * Carte de ligue — la division, dans Progression (§15, D§4).
 *
 * La division vivait UNIQUEMENT dans le bandeau haut, en petit : un objectif long
 * terme relégué au coin de l'écran. Cette carte la donne à lire là où le joueur
 * regarde ce qu'il accomplit — division colorée, seuil suivant chiffré, barre.
 *
 * L'échelle et la couleur viennent de `hud.ts` (`leagueFor`, `LEAGUES`) : une seule
 * source, sinon le bandeau et la carte finiraient par se contredire d'un degré.
 *
 * Le classement hebdomadaire (E.2) n'existe pas encore côté serveur : la carte le dit
 * honnêtement, elle ne simule pas un rang.
 */

import type { CitySnapshot } from '@/core/gameState';
import { n, t } from '@/i18n';
import type { TranslationKey } from '@/i18n';
import { createPanel, createProgress } from './components';
import { LEAGUES, leagueFor, romanDegree } from './hud';
import { icon } from './icons';

export interface LeagueCardHandle {
  readonly el: HTMLElement;
  update(snapshot: CitySnapshot): void;
}

export function createLeagueCard(): LeagueCardHandle {
  const title = document.createElement('h3');
  title.className = 'league-card__title';
  title.append(icon('shield'), t('league.title'));

  const division = document.createElement('p');
  division.className = 'league-card__division';

  const nextLabel = document.createElement('p');
  nextLabel.className = 'league-card__next';

  const progress = createProgress({ value: 0, max: 1, domain: 'coins' });

  const soon = document.createElement('p');
  soon.className = 'league-card__soon';
  soon.textContent = t('league.soon');

  const panel = createPanel({ children: [title, division, nextLabel, progress.el, soon] });
  panel.el.classList.add('league-card');

  return {
    el: panel.el,

    update(snapshot) {
      const current = leagueFor(snapshot.totalProduced);
      const base = t(`leagues.${current.id}` as TranslationKey);
      division.textContent =
        current.degree === undefined ? base : `${base} ${romanDegree(current.degree)}`;
      division.style.color = `var(${current.token})`;

      // Le seuil suivant existe toujours : l'échelle est générée sans dernière
      // marche (§1) — trente degrés de Légende avant d'épuiser quoi que ce soit.
      const next = LEAGUES.find((league) => league.threshold > snapshot.totalProduced);
      if (next === undefined) {
        nextLabel.textContent = '';
        progress.setValue(1);
        return;
      }

      const nextName = t(`leagues.${next.id}` as TranslationKey);
      nextLabel.textContent = t('league.next', {
        name: next.degree === undefined ? nextName : `${nextName} ${romanDegree(next.degree)}`,
        value: n(next.threshold - snapshot.totalProduced),
      });
      progress.setValue(
        Math.min(1, Math.max(0, snapshot.totalProduced / Math.max(1, next.threshold))),
      );
    },
  };
}
